import React from 'react';
import moment from 'moment';
import { SectionCard, EditableInfoField } from './ProfileCommonComponents';
import { isMobileValid, isMobileComplete, isEmailValid, mobileDigitsOf, formatDob } from './utils';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { sanitizeName } from 'components/UserDashboardV2/utils/formatters';
import BasicDetailsSidebar from './BasicDetailsSidebar';
import { useCountryOptions, resolveCountryValue } from './countryList';

const normalize = (v) => (v == null ? "" : String(v).trim());
const normalizePhone = (p) => {
    if (p == null) return "";
    const clean = String(p).replace(/\s+/g, '');
    if (clean === "+91" || clean === "91" || clean === "NA") return "";
    return clean;
};

const EMERGENCY_RELATION_OPTIONS = [
    { label: "Father", value: "Father" },
    { label: "Mother", value: "Mother" },
    { label: "Uncle", value: "Uncle" },
    { label: "Aunt", value: "Aunt" },
    { label: "Brother", value: "Brother" },
    { label: "Sister", value: "Sister" },
    { label: "Others", value: "Others" },
];

const BasicDetailsTab = ({
    isStaffRoute,
    isStaff,
    isTeacher,
    specificUserProfile,
    staffProfile,
    studentSidebar,
    userRowDetails,
    currentAddress,
    permanentAddress,
    editingSection,
    draftProfile,
    savingSection,
    sidebarRef,
    handleEditSection,
    handleCancelSection,
    handleSaveSection,
    handleDraftChange,
}) => {
    const countryOptions = useCountryOptions([
        draftProfile.currentCountry,
        currentAddress?.country,
        draftProfile.permanentCountry,
        permanentAddress?.country,
    ]);

    // 1. Personal Information
    const initialDob = formatDob(specificUserProfile?.basicprofile?.dob);
    const draftDob = formatDob(draftProfile.dob);
    const isPersonalChanged = (
        draftDob !== initialDob ||
        normalize(draftProfile.gender) !== normalize(specificUserProfile?.basicprofile?.gender) ||
        normalize(draftProfile.bloodGroup) !== normalize(specificUserProfile?.basicprofile?.bloodGroup)
    );
    const isPersonalSaveDisabled = !isPersonalChanged;

    // 2. Contact & Address
    const isContactChanged = (
        normalizePhone(draftProfile.phone) !== normalizePhone(specificUserProfile?.phone) ||
        normalize(draftProfile.email) !== normalize(specificUserProfile?.email) ||
        normalize(draftProfile.currentHouseNo) !== normalize(currentAddress?.h_no) ||
        normalize(draftProfile.currentPincode) !== normalize(currentAddress?.pincode) ||
        normalize(draftProfile.currentDistrict) !== normalize(currentAddress?.city) ||
        normalize(draftProfile.currentState) !== normalize(currentAddress?.state) ||
        normalize(draftProfile.currentCountry).toLowerCase() !== normalize(currentAddress?.country).toLowerCase()
    );
    const currentPin = draftProfile.currentPincode ? String(draftProfile.currentPincode).trim() : "";
    const isCurrentPincodeValid = !currentPin || (currentPin.length === 6 && !draftProfile.currentPincodeNotFound);
    const hasCurrentPincodeError = editingSection === 'Contact' && Boolean(currentPin && (currentPin.length < 6 || draftProfile.currentPincodeNotFound));
    const contactPhoneDigits = mobileDigitsOf(draftProfile.phone);
    // Indian numbers: 10-digit valid mobile; other countries: any number.
    const isContactPhoneValid = isMobileComplete(draftProfile.phone);
    // Error only once a digit has been typed after the country code.
    const isContactPhoneError = editingSection === 'Contact' && contactPhoneDigits.length > 0 && !isContactPhoneValid;
    const isContactEmailError = editingSection === 'Contact' && !isEmailValid(draftProfile.email);
    const isContactValid = isContactPhoneValid && isEmailValid(draftProfile.email) && isCurrentPincodeValid;
    const isContactSaveDisabled = !isContactChanged || !isContactValid;

    // 3. Permanent Address (Staff Only)
    const isPermanentChanged = (
        normalize(draftProfile.permanentHouseNo) !== normalize(permanentAddress?.h_no) ||
        normalize(draftProfile.permanentPincode) !== normalize(permanentAddress?.pincode) ||
        normalize(draftProfile.permanentDistrict) !== normalize(permanentAddress?.city) ||
        normalize(draftProfile.permanentState) !== normalize(permanentAddress?.state) ||
        normalize(draftProfile.permanentCountry).toLowerCase() !== normalize(permanentAddress?.country).toLowerCase()
    );
    const permanentPin = draftProfile.permanentPincode ? String(draftProfile.permanentPincode).trim() : "";
    const isPermanentPincodeValid = !permanentPin || (permanentPin.length === 6 && !draftProfile.permanentPincodeNotFound);
    const hasPermanentPincodeError = editingSection === 'PermanentAddress' && Boolean(permanentPin && (permanentPin.length < 6 || draftProfile.permanentPincodeNotFound));
    const isPermanentSaveDisabled = !isPermanentChanged || !isPermanentPincodeValid;

    // 4. Parent / Guardian (Students Only)
    const fatherPhoneDigits = mobileDigitsOf(draftProfile.fatherPhone);
    const motherPhoneDigits = mobileDigitsOf(draftProfile.motherPhone);
    const isFatherPhoneError = editingSection === 'Guardian' && Boolean(fatherPhoneDigits.length > 0) && !isMobileValid(draftProfile.fatherPhone);
    const isFatherEmailError = editingSection === 'Guardian' && Boolean(draftProfile.fatherEmail) && !isEmailValid(draftProfile.fatherEmail);
    const isMotherPhoneError = editingSection === 'Guardian' && Boolean(motherPhoneDigits.length > 0) && !isMobileValid(draftProfile.motherPhone);
    const isMotherEmailError = editingSection === 'Guardian' && Boolean(draftProfile.motherEmail) && !isEmailValid(draftProfile.motherEmail);
    // Father and mother can't share the same phone number / email id.
    const isGuardianPhoneSame = editingSection === 'Guardian' &&
        fatherPhoneDigits.length > 0 && !isFatherPhoneError && !isMotherPhoneError &&
        normalizePhone(draftProfile.fatherPhone) === normalizePhone(draftProfile.motherPhone);
    const isGuardianEmailSame = editingSection === 'Guardian' &&
        Boolean(normalize(draftProfile.fatherEmail)) && !isFatherEmailError && !isMotherEmailError &&
        normalize(draftProfile.fatherEmail).toLowerCase() === normalize(draftProfile.motherEmail).toLowerCase();
    const isGuardianChanged = (
        normalize(draftProfile.fatherName) !== normalize(specificUserProfile?.basicprofile?.fatherName) ||
        normalizePhone(draftProfile.fatherPhone) !== normalizePhone(specificUserProfile?.basicprofile?.fatherPhone) ||
        normalize(draftProfile.fatherEmail) !== normalize(specificUserProfile?.basicprofile?.fatherEmail) ||
        normalize(draftProfile.motherName) !== normalize(specificUserProfile?.basicprofile?.motherName) ||
        normalizePhone(draftProfile.motherPhone) !== normalizePhone(specificUserProfile?.basicprofile?.motherPhone) ||
        normalize(draftProfile.motherEmail) !== normalize(specificUserProfile?.basicprofile?.motherEmail)
    );
    const isGuardianValid = (
        (!fatherPhoneDigits.length || isMobileValid(draftProfile.fatherPhone)) &&
        (!draftProfile.fatherEmail || isEmailValid(draftProfile.fatherEmail)) &&
        (!motherPhoneDigits.length || isMobileValid(draftProfile.motherPhone)) &&
        (!draftProfile.motherEmail || isEmailValid(draftProfile.motherEmail))
    );
    const isGuardianSaveDisabled = !isGuardianChanged || !isGuardianValid || isGuardianPhoneSame || isGuardianEmailSame;

    // 5. Emergency Contact
    const emergencyDigits = mobileDigitsOf(draftProfile.emergencyContactPhone);
    const isEmergencyNameGiven = Boolean(
        draftProfile.emergencyContactName &&
        String(draftProfile.emergencyContactName).trim().length > 0 &&
        draftProfile.emergencyContactName !== 'NA'
    );
    const isEmergencyPhoneGiven = isMobileComplete(draftProfile.emergencyContactPhone);
    const isEmergencyRelationDisabled = !isEmergencyNameGiven || !isEmergencyPhoneGiven;

    const isEmergencyPhoneError = editingSection === 'Emergency' && Boolean(draftProfile.emergencyContactPhone && emergencyDigits.length > 0) && !isMobileValid(draftProfile.emergencyContactPhone);
    const isEmergencyChanged = (
        normalize(draftProfile.emergencyContactName) !== normalize(specificUserProfile?.basicprofile?.emergencyContactName) ||
        normalizePhone(draftProfile.emergencyContactPhone) !== normalizePhone(specificUserProfile?.basicprofile?.emergencyPhone) ||
        normalize(draftProfile.emergencyContactRelationship) !== normalize(specificUserProfile?.basicprofile?.relationship)
    );
    const isEmergencyValid = (
        (!emergencyDigits.length || isMobileValid(draftProfile.emergencyContactPhone)) &&
        (!draftProfile.emergencyContactName || Boolean(String(draftProfile.emergencyContactName).trim()))
    );
    const isEmergencySaveDisabled = !isEmergencyChanged || !isEmergencyValid;

    const emergencyCardRef = React.useRef(null);

    React.useEffect(() => {
        if (editingSection === 'Emergency' && isEmergencyRelationDisabled && draftProfile.emergencyContactRelationship) {
            handleDraftChange('emergencyContactRelationship', '');
        }
    }, [editingSection, isEmergencyRelationDisabled, draftProfile.emergencyContactRelationship, handleDraftChange]);

    React.useEffect(() => {
        if (editingSection === 'Emergency' && emergencyCardRef.current) {
            setTimeout(() => {
                emergencyCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 60);
        }
    }, [editingSection]);

    return (
        <div style={{ display: "flex", gap: fluid(24), alignItems: "stretch", flex: 1, minHeight: 0 }}>
            {/* Left Scrollable Column */}
            <div
                className="staff-profile-info-scroll"
                style={{
                    flex: `1 1 ${fluid(640)}`,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(16),
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                }}
            >
                {/* Personal Information */}
                <SectionCard
                    title="Personal information"
                    isEditing={editingSection === 'Personal'}
                    onEdit={() => handleEditSection('Personal')}
                    onCancel={handleCancelSection}
                    onSave={handleSaveSection}
                    saving={savingSection}
                    saveDisabled={isPersonalSaveDisabled}
                >
                    <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                        <EditableInfoField
                            label="Date of birth"
                            type="date"
                            isEditing={editingSection === 'Personal'}
                            value={editingSection === 'Personal' ? draftProfile.dob : specificUserProfile?.basicprofile?.dob}
                            onChange={(val) => handleDraftChange('dob', val)}
                        />
                        <EditableInfoField
                            label="Gender"
                            type="dropdown"
                            placeholder="Select gender"
                            options={[{ label: "Male", value: "Male" }, { label: "Female", value: "Female" }, { label: "Others", value: "Others" }]}
                            isEditing={editingSection === 'Personal'}
                            value={editingSection === 'Personal' ? draftProfile.gender : specificUserProfile?.basicprofile?.gender}
                            onChange={(val) => handleDraftChange('gender', val || '')}
                        />
                        <EditableInfoField
                            label="Blood group"
                            type="dropdown"
                            placeholder="Select blood group"
                            options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(v => ({ label: v, value: v }))}
                            isEditing={editingSection === 'Personal'}
                            value={editingSection === 'Personal' ? draftProfile.bloodGroup : specificUserProfile?.basicprofile?.bloodGroup}
                            onChange={(val) => handleDraftChange('bloodGroup', val || '')}
                        />
                    </div>
                </SectionCard>

                {/* Contact & Address */}
                <SectionCard
                    title="Contact & address"
                    isEditing={editingSection === 'Contact'}
                    onEdit={() => handleEditSection('Contact')}
                    onCancel={handleCancelSection}
                    onSave={handleSaveSection}
                    saving={savingSection}
                    saveDisabled={isContactSaveDisabled}
                >
                    <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: fluid(2) }}>
                        <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                            <EditableInfoField
                                type="phone"
                                label="Phone No."
                                required={true}
                                isEditing={editingSection === 'Contact'}
                                value={editingSection === 'Contact' ? draftProfile.phone : specificUserProfile?.phone}
                                onChange={(val) => handleDraftChange('phone', val)}
                                error={isContactPhoneError}
                            />
                            <EditableInfoField
                                type="text"
                                label="Email"
                                isEditing={editingSection === 'Contact'}
                                value={editingSection === 'Contact' ? draftProfile.email : specificUserProfile?.email}
                                onChange={(val) => handleDraftChange('email', val)}
                                error={isContactEmailError}
                            />
                        </div>
                        {(isContactPhoneError || isContactEmailError) && (
                            <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                    {isContactPhoneError && (
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                            {!contactPhoneDigits ? "Phone number is required" : "Enter a valid phone number"}
                                        </span>
                                    )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                    {isContactEmailError && (
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                            Enter a valid email id
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: fluid(2) }}>
                        <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                            <EditableInfoField
                                type="text"
                                label="Address"
                                isEditing={editingSection === 'Contact'}
                                value={editingSection === 'Contact' ? draftProfile.currentHouseNo : currentAddress?.h_no}
                                flex={2}
                                onChange={(val) => handleDraftChange('currentHouseNo', val)}
                            />
                            <EditableInfoField
                                type="text"
                                label="Pincode"
                                isEditing={editingSection === 'Contact'}
                                value={editingSection === 'Contact' ? draftProfile.currentPincode : currentAddress?.pincode}
                                flex={1}
                                onChange={(val) => handleDraftChange('currentPincode', String(val || '').replace(/\D/g, '').slice(0, 6))}
                                error={hasCurrentPincodeError}
                            />
                        </div>
                        {hasCurrentPincodeError && (
                            <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                <div style={{ flex: 2, minWidth: 0 }} />
                                <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                        Enter a valid pincode
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                        <EditableInfoField
                            type="text"
                            label="District"
                            isEditing={editingSection === 'Contact'}
                            value={editingSection === 'Contact' ? draftProfile.currentDistrict : currentAddress?.city}
                            onChange={(val) => handleDraftChange('currentDistrict', val)}
                        />
                        <EditableInfoField
                            type="text"
                            label="State"
                            isEditing={editingSection === 'Contact'}
                            value={editingSection === 'Contact' ? draftProfile.currentState : currentAddress?.state}
                            onChange={(val) => handleDraftChange('currentState', val)}
                        />
                        <EditableInfoField
                            type="dropdown"
                            label="Country"
                            showSearch={true}
                            options={countryOptions}
                            placeholder="Select country"
                            isEditing={editingSection === 'Contact'}
                            value={
                                editingSection === 'Contact'
                                    ? resolveCountryValue(draftProfile.currentCountry, countryOptions)
                                    : (currentAddress?.country || "")
                            }
                            onChange={(val) => handleDraftChange('currentCountry', val || '')}
                        />
                    </div>
                </SectionCard>

                {/* Permanent Address — Staff Only */}
                {isStaffRoute && (
                    <SectionCard
                        title="Permanent address"
                        isEditing={editingSection === 'PermanentAddress'}
                        onEdit={() => handleEditSection('PermanentAddress')}
                        onCancel={handleCancelSection}
                        onSave={handleSaveSection}
                        saving={savingSection}
                        saveDisabled={isPermanentSaveDisabled}
                    >
                        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: fluid(2) }}>
                            <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                                <EditableInfoField
                                    type="text"
                                    label="Address"
                                    isEditing={editingSection === 'PermanentAddress'}
                                    value={editingSection === 'PermanentAddress' ? draftProfile.permanentHouseNo : permanentAddress?.h_no}
                                    flex={2}
                                    onChange={(val) => handleDraftChange('permanentHouseNo', val)}
                                />
                                <EditableInfoField
                                    type="text"
                                    label="Pincode"
                                    isEditing={editingSection === 'PermanentAddress'}
                                    value={editingSection === 'PermanentAddress' ? draftProfile.permanentPincode : permanentAddress?.pincode}
                                    flex={1}
                                    onChange={(val) => handleDraftChange('permanentPincode', String(val || '').replace(/\D/g, '').slice(0, 6))}
                                    error={hasPermanentPincodeError}
                                />
                            </div>
                            {hasPermanentPincodeError && (
                                <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ flex: 2, minWidth: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                            Enter a valid pincode
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                            <EditableInfoField
                                type="text"
                                label="District"
                                isEditing={editingSection === 'PermanentAddress'}
                                value={editingSection === 'PermanentAddress' ? draftProfile.permanentDistrict : permanentAddress?.city}
                                onChange={(val) => handleDraftChange('permanentDistrict', val)}
                            />
                            <EditableInfoField
                                type="text"
                                label="State"
                                isEditing={editingSection === 'PermanentAddress'}
                                value={editingSection === 'PermanentAddress' ? draftProfile.permanentState : permanentAddress?.state}
                                onChange={(val) => handleDraftChange('permanentState', val)}
                            />
                            <EditableInfoField
                                type="dropdown"
                                label="Country"
                                showSearch={true}
                                options={countryOptions}
                                placeholder="Select country"
                                isEditing={editingSection === 'PermanentAddress'}
                                value={
                                    editingSection === 'PermanentAddress'
                                        ? resolveCountryValue(draftProfile.permanentCountry, countryOptions)
                                        : (permanentAddress?.country || "")
                                }
                                onChange={(val) => handleDraftChange('permanentCountry', val || '')}
                            />
                        </div>
                    </SectionCard>
                )}

                {/* Parent / Guardian Information — Students Only */}
                {!isStaffRoute && (
                    <SectionCard
                        title="Parent / Guardian information"
                        isEditing={editingSection === 'Guardian'}
                        onEdit={() => handleEditSection('Guardian')}
                        onCancel={handleCancelSection}
                        onSave={handleSaveSection}
                        saving={savingSection}
                        saveDisabled={isGuardianSaveDisabled}
                    >
                        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: fluid(2) }}>
                            <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                                <EditableInfoField
                                    type="text"
                                    label="Father's name"
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.fatherName : specificUserProfile?.basicprofile?.fatherName}
                                    onChange={(val) => handleDraftChange('fatherName', sanitizeName(val))}
                                    badge
                                />
                                <EditableInfoField
                                    type="phone"
                                    label="Phone No."
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.fatherPhone : specificUserProfile?.basicprofile?.fatherPhone}
                                    onChange={(val) => handleDraftChange('fatherPhone', val)}
                                    error={isFatherPhoneError || isGuardianPhoneSame}
                                />
                                <EditableInfoField
                                    type="text"
                                    label="Email ID"
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.fatherEmail : specificUserProfile?.basicprofile?.fatherEmail}
                                    onChange={(val) => handleDraftChange('fatherEmail', val)}
                                    error={isFatherEmailError || isGuardianEmailSame}
                                />
                            </div>
                            {(isFatherPhoneError || isFatherEmailError) && (
                                <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ flex: 1, minWidth: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        {isFatherPhoneError && (
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                                Enter a valid phone number
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        {isFatherEmailError && (
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                                Enter a valid email id
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: fluid(2) }}>
                            <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                                <EditableInfoField
                                    type="text"
                                    label="Mother's name"
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.motherName : specificUserProfile?.basicprofile?.motherName}
                                    onChange={(val) => handleDraftChange('motherName', sanitizeName(val))}
                                />
                                <EditableInfoField
                                    type="phone"
                                    label="Phone No."
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.motherPhone : specificUserProfile?.basicprofile?.motherPhone}
                                    onChange={(val) => handleDraftChange('motherPhone', val)}
                                    error={isMotherPhoneError || isGuardianPhoneSame}
                                />
                                <EditableInfoField
                                    type="text"
                                    label="Email ID"
                                    isEditing={editingSection === 'Guardian'}
                                    value={editingSection === 'Guardian' ? draftProfile.motherEmail : specificUserProfile?.basicprofile?.motherEmail}
                                    onChange={(val) => handleDraftChange('motherEmail', val)}
                                    error={isMotherEmailError || isGuardianEmailSame}
                                />
                            </div>
                            {(isMotherPhoneError || isMotherEmailError || isGuardianPhoneSame || isGuardianEmailSame) && (
                                <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ flex: 1, minWidth: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        {(isMotherPhoneError || isGuardianPhoneSame) && (
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                                {isMotherPhoneError ? "Enter a valid phone number" : "Father's and mother's phone number can't be same"}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        {(isMotherEmailError || isGuardianEmailSame) && (
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                                {isMotherEmailError ? "Enter a valid email id" : "Father's and mother's email id can't be same"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </SectionCard>
                )}

                {/* Emergency Contact */}
                <div ref={emergencyCardRef}>
                    <SectionCard
                        title="Emergency contact"
                        isEditing={editingSection === 'Emergency'}
                        onEdit={() => handleEditSection('Emergency')}
                        onCancel={handleCancelSection}
                        onSave={handleSaveSection}
                        saving={savingSection}
                        saveDisabled={isEmergencySaveDisabled}
                        style={{
                            paddingBottom: editingSection === 'Emergency' ? fluid(36) : fluid(20),
                        }}
                    >
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: fluid(2),
                            paddingBottom: editingSection === 'Emergency' ? fluid(16) : 0,
                        }}>
                            <div style={{ display: "flex", gap: fluid(16), width: "100%" }}>
                                <EditableInfoField
                                    type="text"
                                    label="Name"
                                    isEditing={editingSection === 'Emergency'}
                                    value={editingSection === 'Emergency' ? draftProfile.emergencyContactName : specificUserProfile?.basicprofile?.emergencyContactName}
                                    onChange={(val) => handleDraftChange('emergencyContactName', sanitizeName(val))}
                                />
                                <EditableInfoField
                                    type="phone"
                                    label="Phone No."
                                    isEditing={editingSection === 'Emergency'}
                                    value={
                                        editingSection === 'Emergency'
                                            ? (draftProfile.emergencyContactPhone || "+91")
                                            : (specificUserProfile?.basicprofile?.emergencyPhone || "")
                                    }
                                    onChange={(val) => handleDraftChange('emergencyContactPhone', val || "+91")}
                                    error={isEmergencyPhoneError}
                                />
                                <EditableInfoField
                                    type="dropdown"
                                    label="Relation"
                                    options={EMERGENCY_RELATION_OPTIONS}
                                    placeholder="Select relation"
                                    isEditing={editingSection === 'Emergency'}
                                    disabled={editingSection === 'Emergency' && isEmergencyRelationDisabled}
                                    value={
                                        editingSection === 'Emergency'
                                            ? (isEmergencyRelationDisabled
                                                ? undefined
                                                : (EMERGENCY_RELATION_OPTIONS.find(opt => opt.value.toLowerCase() === String(draftProfile.emergencyContactRelationship || '').toLowerCase())?.value || draftProfile.emergencyContactRelationship || undefined))
                                            : (specificUserProfile?.basicprofile?.relationship || "")
                                    }
                                    onChange={(val) => handleDraftChange('emergencyContactRelationship', val || '')}
                                />
                            </div>
                            {isEmergencyPhoneError && (
                                <div style={{ display: "flex", gap: fluid(16), width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ flex: 1, minWidth: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0, textAlign: "right", paddingRight: fluid(16) }}>
                                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#FF414D" }}>
                                            Enter a valid phone number
                                        </span>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }} />
                                </div>
                            )}
                        </div>
                    </SectionCard>
                </div>
            </div>

            {/* Right Sidebar Column */}
            <BasicDetailsSidebar
                sidebarRef={sidebarRef}
                isStaff={isStaff}
                isTeacher={isTeacher}
                isStaffRoute={isStaffRoute}
                specificUserProfile={specificUserProfile}
                staffProfile={staffProfile}
                studentSidebar={studentSidebar}
                userRowDetails={userRowDetails}
            />
        </div>
    );
};

export default BasicDetailsTab;
